import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileUp } from 'lucide-react';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { readFileText } from '../../lib/upload';
import { Button, InlineError, Modal, Notice } from '../ui';

const MAX_CSV_BYTES = 5 * 1024 * 1024;

export function CsvImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const imp = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_CSV_BYTES) throw new Error('That CSV is larger than 5 MB. Split it into smaller files.');
      const csv = await readFileText(file);
      return api('POST /api/contacts/import', { body: { csv } });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.contactsRoot });
      void qc.invalidateQueries({ queryKey: qk.contactDuplicates });
      void qc.invalidateQueries({ queryKey: qk.contactSuggestions });
    },
  });

  const close = () => {
    imp.reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={close} title="Import contacts from CSV" footer={<Button onClick={close}>{imp.isSuccess ? 'Done' : 'Cancel'}</Button>}>
      <div className="space-y-4 text-[0.8125rem]">
        <div className="space-y-2 text-muted">
          <p>Use a CSV file with a header row. Expected columns:</p>
          <code className="block overflow-x-auto rounded-xl bg-raised px-3 py-2 font-mono text-xs text-ink">name,email,company,tags,notes</code>
          <p>
            <strong className="text-ink">email</strong> is required (separate several addresses or tags with semicolons). Rows whose email is already in your contacts are skipped; rows without a valid email are listed below. Exports from Google or Outlook usually work too.
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          aria-label="Choose CSV file"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) imp.mutate(f);
            e.target.value = '';
          }}
        />
        <Button variant="primary" icon={<FileUp className="h-4 w-4" />} loading={imp.isPending} onClick={() => inputRef.current?.click()} className="max-md:h-11">
          {imp.isSuccess ? 'Import another file' : 'Choose CSV file'}
        </Button>
        {imp.isError && <InlineError error={imp.error} />}
        {imp.data && (
          <div className="space-y-2" role="status">
            <Notice tone={imp.data.imported > 0 ? 'success' : 'warn'}>
              Imported <strong>{imp.data.imported}</strong> {imp.data.imported === 1 ? 'contact' : 'contacts'}, skipped <strong>{imp.data.skipped}</strong>.
            </Notice>
            {imp.data.errors.length > 0 && (
              <div className="rounded-xl border border-line bg-raised p-3">
                <p className="mb-1 font-medium text-ink">Problems ({imp.data.errors.length})</p>
                <ul className="max-h-40 list-disc space-y-0.5 overflow-y-auto pl-5 text-xs text-muted scroll-thin">
                  {imp.data.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
